import { formatTime, useSimpleTimer } from '@/shared/utils/timer';
import { Button, Text } from '@noorden-insurtech/alba-design-system';
import React, { useRef, useState } from 'react';
import { ErrorMessage } from './error-message.component';

interface TwoFactorFormProps {
  onSubmit: (code: string) => Promise<void>;
  onResend?: () => Promise<void> | void;
  loading?: boolean;
  error?: string;
  totalSeconds?: number;
}

export function TwoFactorForm({
  onSubmit,
  onResend,
  loading = false,
  error,
  totalSeconds = 300,
}: TwoFactorFormProps) {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const { secondsLeft, reset } = useSimpleTimer(totalSeconds);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (index: number, value: string) => {
    const onlyDigit = value.replace(/\D/g, '').slice(0, 1);
    const newDigits = [...digits];
    newDigits[index] = onlyDigit;
    setDigits(newDigits);

    if (onlyDigit && index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 6);
    if (!pasted) return;

    const newDigits = pasted.split('');
    while (newDigits.length < 6) newDigits.push('');
    setDigits(newDigits);
    const lastIndex = Math.min(pasted.length, 6) - 1;
    if (lastIndex >= 0) inputsRef.current[lastIndex]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = digits.join('');
    await onSubmit(code);
  };

  const codeFilled = digits.every(d => d !== '');
  const canResend = secondsLeft === 0;

  return (
    <form onSubmit={handleSubmit} className='flex flex-col gap-6 w-full'>
      <div className='flex w-full items-center gap-2 sm:gap-3'>
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={el => {
              inputsRef.current[index] = el;
            }}
            type='text'
            inputMode='numeric'
            pattern='[0-9]*'
            value={digit}
            onChange={e => handleChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            onPaste={handlePaste}
            className='h-14 w-full min-w-0 max-w-14 flex-1 text-center text-xl sm:text-[20px] font-bold rounded-lg sm:rounded-xl border border-alba-gray-300 bg-white text-alba-black-500 outline-none focus:border-alba-blue-500 focus:ring-2 focus:ring-alba-blue-200'
            placeholder='-'
            maxLength={1}
            required
          />
        ))}
      </div>

      {canResend ? (
        <div className='flex items-center gap-2'>
          <Text size='xs' className='text-[#6b7280]'>
            Não recebeu o código?
          </Text>
          {onResend && (
            <button
              type='button'
              onClick={async () => {
                await onResend();
                reset();
              }}
              className='text-[#129440] text-xs font-semibold hover:underline'
            >
              Reenviar.
            </button>
          )}
        </div>
      ) : (
        <Text size='xs' className='text-[#6b7280]'>
          Código válido por {formatTime(secondsLeft)}
        </Text>
      )}

      {error && <ErrorMessage message={error} />}

      <Button
        type='submit'
        variant='primary'
        size='large'
        disabled={loading || !codeFilled}
        className='w-full'
      >
        {loading ? 'Validando...' : 'Continuar'}
      </Button>
    </form>
  );
}
